// Cel-shading helpers: stepped toon lighting + inverted-hull ink outlines. Original technique code, no external assets.
import * as THREE from 'three/webgpu';
import { mergeVertices } from 'three/addons/utils/BufferGeometryUtils.js';
import { color, normalLocal, positionLocal, float } from 'three/tsl';
import { ANIME } from './animeLook';

export type RenderStyle = 'cel' | 'standard';

let gradient: THREE.DataTexture | null = null;
/** 3-band light ramp (shadow / mid / lit) sampled with nearest filtering for hard cel bands. */
export function toonGradient(): THREE.DataTexture {
  if (gradient) return gradient;
  const data = new Uint8Array([90, 90, 90, 255, 175, 175, 175, 255, 255, 255, 255, 255]);
  gradient = new THREE.DataTexture(data, 3, 1, THREE.RGBAFormat);
  gradient.minFilter = THREE.NearestFilter; gradient.magFilter = THREE.NearestFilter;
  gradient.generateMipmaps = false; gradient.needsUpdate = true;
  ANIME.registerRamp(gradient, [80, 155, 255]); // ?look=anime: crisper terminator; shadow colour comes from the (map) fill light
  return gradient;
}

export function toonMaterial(params: { color?: THREE.ColorRepresentation; emissive?: THREE.ColorRepresentation } = {}): THREE.MeshToonNodeMaterial {
  const m = new THREE.MeshToonNodeMaterial({ gradientMap: toonGradient() });
  if (params.color !== undefined) m.color.set(params.color);
  if (params.emissive !== undefined) m.emissive.set(params.emissive);
  return m;
}

/** Same shape with smoothed (position-welded) normals, so the extruded hull has no gaps at hard edges. */
export function outlineGeometry(src: THREE.BufferGeometry): THREE.BufferGeometry {
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', src.getAttribute('position').clone());
  if (src.index) g.setIndex(src.index.clone());
  const welded = mergeVertices(g.index ? g.toNonIndexed() : g, 1e-3);
  welded.computeVertexNormals();
  welded.computeBoundingSphere();
  return welded;
}

const outlineMats = new Map<string, THREE.MeshBasicNodeMaterial>();
/** Back-face hull pushed out along the normal by `thickness` (local units). */
export function outlineMaterial(thickness: number, ink = 0x0b0612): THREE.MeshBasicNodeMaterial {
  const key = `${thickness}:${ink}`;
  let m = outlineMats.get(key);
  if (!m) {
    m = new THREE.MeshBasicNodeMaterial({ side: THREE.BackSide });
    m.colorNode = color(ink);
    m.positionNode = positionLocal.add(normalLocal.mul(float(thickness)));
    outlineMats.set(key, m);
  }
  return m;
}

/** Add ink outlines to every mesh under root (non-instanced). Skips transparent/additive materials. */
export function addOutlines(root: THREE.Object3D, thickness: number): void {
  const meshes: THREE.Mesh[] = [];
  root.traverse((o) => { const m = o as THREE.Mesh; if (m.isMesh && !(m.material as THREE.Material).transparent && !m.userData.noOutline) meshes.push(m); });
  const mat = outlineMaterial(thickness);
  for (const m of meshes) {
    const o = new THREE.Mesh(outlineGeometry(m.geometry), mat);
    o.castShadow = false; o.receiveShadow = false;
    m.add(o);
  }
}

/** Outline twin for an InstancedMesh: shares the instance matrices, so only `count` needs syncing. */
export function instancedOutline(src: THREE.InstancedMesh, thickness: number, cap: number): THREE.InstancedMesh {
  const o = new THREE.InstancedMesh(outlineGeometry(src.geometry), outlineMaterial(thickness), cap);
  o.instanceMatrix = src.instanceMatrix;
  o.frustumCulled = false; o.count = src.count;
  return o;
}
